import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "netflix",
  app: "Netflix",
  title: "How to block Netflix on iPhone — stopping after one episode",
  h1: "How to block Netflix",
  description:
    "Screen Time steps, why the countdown between episodes is the whole problem, and the two settings that hand the decision back to you.",
  targets: [
    "how to block netflix on iphone",
    "stop binge watching netflix",
    "netflix autoplay off",
    "how to limit netflix time",
  ],
  published: "2026-09-03",
  standfirst:
    "You did not decide to watch four episodes. You decided to watch one, and then failed to make three separate decisions to stop.",
  takeaways: [
    "Turn off both autoplay settings in your account. It is the single change on this page that does most of the work, and it takes a minute.",
    "Netflix's design removes the gap between episodes, which is where stopping used to happen.",
    "The useful limit is a bedtime rather than a budget: Downtime beats a daily amount for anything watched in the evening.",
  ],
  mechanic: [
    "Television used to have a natural stopping point built into its distribution: the episode ended, and the next one was next week. Everything about the intervening gap — the credits, the wait, the going to bed — was a place where a person decided whether to continue, and mostly they did not, because continuing was not on offer.",
    "Netflix's contribution was to remove that gap and replace it with a countdown. The next episode begins in a small number of seconds unless you intervene, which inverts the decision: stopping is now the action that requires effort, and continuing is what happens if you do nothing. Anyone who has watched four episodes did not choose four; they failed to interrupt three times, at eleven at night, while tired.",
    "The second piece is autoplay on the browse screen, where trailers begin playing under the cursor. That converts browsing — which used to be a low-stakes activity you could leave — into a continuous stream of content, so even the not-watching is now watching.",
  ],
  limitFails:
    "A daily time limit on Netflix fires mid-episode, which is the worst possible moment to ask somebody to stop and the most likely to be dismissed — nobody abandons a programme with eleven minutes left. It is also measuring the wrong unit: the problem is not that two hours were spent, it is that the second hour was never chosen, and a budget that permits two hours does not care whether they were decided on or fallen into.",
  instead: [
    "Turn off both autoplay options: Account → Profile → Playback settings → uncheck autoplay next episode and autoplay previews. It applies per profile, so do it on each one.",
    "Use Downtime rather than an App Limit, set to the hour you actually want to be asleep. It arrives between episodes rather than inside one.",
    "Decide the number before you start and say it out loud to whoever is in the room. This sounds trivial and is the most reliable thing on the list.",
    "Watch on a television rather than a phone if you can. A screen you have to get up from is a screen you get up from.",
  ],
  verses: [
    {
      verse: {
        text: "So teach us to count our days, that we may gain a heart of wisdom.",
        ref: "Psalm 90:12",
      },
      note: "The request is for arithmetic, oddly enough — a psalm that has spent eleven verses on how brief a life is asks not for more time but for the ability to count what there is. Wisdom is described as the result of the counting rather than as a separate gift, which makes it an unusually practical verse for anything measured in hours.",
    },
    {
      verse: {
        text: "Whereas you don't know what your life will be like tomorrow. For what is your life? For you are a vapor that appears for a little time and then vanishes away.",
        ref: "James 4:14",
      },
      note: "James is arguing against confident plans rather than against rest, so this is not a verse against watching television. What it supplies is the scale: a vapour, appearing briefly. Held next to an evening that was going to be one episode and became four, it is less a rebuke than a piece of information.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Two checkboxes",
      id: "two-checkboxes",
    },
    {
      type: "p",
      text: "Netflix's playback settings contain the only intervention on this page with a measurable effect: autoplay next episode, and autoplay previews. Both are on by default, both can be turned off, and turning them off restores the gap that used to do the deciding.",
    },
    {
      type: "p",
      text: "They are per profile and they live on the website rather than in the app, which is presumably not an accident.",
    },
    {
      type: "h2",
      text: "Downtime beats a budget",
      id: "downtime-beats-a-budget",
    },
    {
      type: "p",
      text: "For evening viewing, a schedule is a better instrument than an allowance. A limit that fires at 43 minutes fires inside a programme; a Downtime that starts at eleven arrives at a point where stopping is already natural.",
    },
    {
      type: "note",
      text: "Same logic as the bedtime you set for a child, and for the same reason: the decision is made in advance by a version of you who is not tired.",
    },
    {
      type: "h2",
      text: "This one is allowed to be fine",
      id: "this-one-is-allowed-to-be-fine",
    },
    {
      type: "p",
      text: "Unlike most of the apps on this site, watching a programme is a normal way for an adult to spend an evening, and a page that treats it as a moral failure is not being honest. The problem is narrow and specific: the hour that was not chosen.",
    },
    {
      type: "p",
      text: "Which is why the fix here is about the gap rather than about abstinence — closer to {a rhythm of rest} than to a restriction.",
      links: {
        "a rhythm of rest": "/blog/digital-sabbath",
      },
    },
  ],
  faq: [
    {
      q: "How do I stop Netflix autoplaying the next episode?",
      a: "Account → Profile → Playback settings, and uncheck both autoplay next episode and autoplay previews. It is per profile and lives on the website rather than in the app.",
    },
    {
      q: "How do I block Netflix on iPhone?",
      a: "Screen Time → Downtime, set to the hour you want to be asleep, with Netflix removed from Always Allowed. Downtime works better than an App Limit here because it does not fire mid-episode.",
    },
    {
      q: "Is binge-watching bad?",
      a: "Watching a programme is a normal evening. The narrow problem is the hour nobody chose — three failures to interrupt a countdown rather than a decision to watch four episodes.",
    },
  ],
  relatedApps: ["youtube", "twitch"],
  related: ["digital-sabbath", "how-to-stop-scrolling-at-night"],
  relatedPrayers: ["prayer-before-bed", "prayer-for-self-control"],
};
